//
// useCaseRefreshOnPush
//
// Subscribes a single handler to the push notification types that mean
// "case data changed, refresh it". caseUpdate is in the set because a case
// update also produces an automated case message, so it concerns the chat too.
// caseSilentUpdate is the silent one every viewer of the case gets, whether or not
// the change was addressed to them.
// includeNewCase adds newCase, which only matters for the cases list (never for
// an already-open case). Pass a memoized filter (e.g. { csid }) to scope to one
// case. Subscribes only while a live channel exists, mirroring usePushEvent.
//
import { useEffect } from 'react';

import usePush from './usePush';

const CASE_EVENT_TYPES = [
  'caseUpdate', 'caseSilentUpdate', 'caseMsg', 'casePic', 'caseAudio', 'caseAlert',
];
const LIST_EVENT_TYPES = ['newCase', ...CASE_EVENT_TYPES];

const useCaseRefreshOnPush = (handler, options = {}) => {
  const { enabled = true, includeNewCase = false, filter } = options;
  const { push } = usePush(false);
  const subscribe = push?.subscribe;
  const wsConnected = push?.wsConnected;
  const swAvailable = push?.swAvailable;

  useEffect(
    () => {
      if ((!enabled) || (!subscribe) || (!handler)) return undefined;
      if ((!wsConnected) && (!swAvailable)) return undefined;

      const types = includeNewCase ? LIST_EVENT_TYPES : CASE_EVENT_TYPES;
      const unsubscribes = types.map((type) => subscribe(type, handler, filter));

      return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    },
    [enabled, includeNewCase, handler, filter, subscribe, wsConnected, swAvailable],
  );
};

export default useCaseRefreshOnPush;
